<template>
  <div class="science-of-reading-section">
    <h2 class="text-h6 font-weight-bold text-secondary mb-8">
      <a
        href="#"
        class="text-primary text-decoration-underline section-subtitle-link glossary-link"
        data-glossary-term="science of reading snapshot"
        >Science of Reading Snapshot</a
      >
    </h2>

    <h3 class="font-weight-bold text-secondary mb-2">
      What are
      <a
        href="#"
        class="text-primary text-decoration-underline glossary-link"
        data-glossary-term="non-negotiable"
      >
        non-negotiables</a
      >?
    </h3>

    <!-- Science of Reading Snapshot Table -->
    <DataTable
      :headers="dynamicTableHeaders"
      :items="scienceOfReadingItems"
      :skillName="true"
      classes="mt-6"
      v-if="scienceOfReadingItems.length > 0"
    />
    <div
      v-else-if="report && !loading && scienceOfReadingItems.length === 0"
      class="text-center my-4"
    >
      <p>Science of Reading data is not available for this report.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import DataTable from '../shared/DataTable.vue';
  import type { Report } from '../../store';
  import { formatPercentageValue } from '../../lib/status-utils';

  interface Props {
    report: Report | null;
    loading?: boolean;
    overview?: any; // Optional overview parameter for grade-band specific data
  }

  const props = withDefaults(defineProps<Props>(), {
    loading: false,
    overview: undefined,
  });

  // Helper function to decode HTML entities in scores
  const decodeHtmlEntities = (str: string): string => {
    if (!str) return str;
    const textArea = document.createElement('textarea');
    textArea.innerHTML = str;
    return textArea.value;
  };

  const dynamicTableHeaders = computed(() => {
    // Use the provided overview if available, otherwise fall back to the first overview
    const targetOverview = props.overview || props.report?.overviews?.[0];
    if (!targetOverview?.report_cards) return [];
    const sortedReportCards = [...targetOverview.report_cards].sort(
      (a, b) => a.grade.order - b.grade.order,
    );
    return sortedReportCards.map((rc) => rc.grade.shortcode || rc.grade.slug);
  });

  const scienceOfReadingItems = computed(() => {
    // Use the provided overview if available, otherwise fall back to the first overview
    const targetOverview = props.overview || props.report?.overviews?.[0];
    if (
      !targetOverview?.criteria_groups ||
      dynamicTableHeaders.value.length === 0
    )
      return [];

    // Find the Science of Reading group
    const sorGroup = targetOverview.criteria_groups.find(
      (cg: any) =>
        cg.title.includes('Science of Reading') ||
        cg.slug === 'science-of-reading',
    );

    if (!sorGroup) return [];

    const items = sorGroup.criteria
      .map((criterion: any) => {
        const item: { [key: string]: any } = {
          skill: criterion.label,
          subtitle: isNonNegotiableCriterion(criterion) ? 'Non-negotiable' : '',
        };

        // Map scores to grade headers
        dynamicTableHeaders.value.forEach((gradeShortCode) => {
          const scoreEntry = criterion.scores.find(
            (s: any) => s.grade.shortcode === gradeShortCode,
          );

          if (scoreEntry && scoreEntry.score) {
            // Decode HTML entities and clean up the score
            let decodedScore = decodeHtmlEntities(scoreEntry.score);

            // Handle cases where score is "0%" and review_status indicates not available
            if (
              decodedScore === '0%' &&
              (typeof scoreEntry.review_status === 'string'
                ? scoreEntry.review_status === 'not-claimed' ||
                  scoreEntry.review_status === 'not-eligible' ||
                  scoreEntry.review_status === 'did-not-review'
                : scoreEntry.review_status?.slug === 'not-claimed' ||
                  scoreEntry.review_status?.slug === 'not-eligible' ||
                  scoreEntry.review_status?.slug === 'did-not-review')
            ) {
              item[gradeShortCode] = 'N/A';
            } else {
              item[gradeShortCode] = formatPercentageValue(decodedScore);
            }
          } else {
            item[gradeShortCode] = 'N/A';
          }
        });

        return item;
      })
      .sort((a: any, b: any) => {
        // Sort by the order field if available, otherwise alphabetically
        const criterionA = sorGroup.criteria.find(
          (c: any) => c.label === a.skill,
        );
        const criterionB = sorGroup.criteria.find(
          (c: any) => c.label === b.skill,
        );
        if (
          criterionA?.order !== undefined &&
          criterionB?.order !== undefined
        ) {
          return criterionA.order - criterionB.order;
        }
        return a.skill && b.skill ? a.skill.localeCompare(b.skill) : 0;
      });

    return items;
  });

  function isNonNegotiableCriterion(criterion: any): boolean {
    // Check if the criterion has explicit non_negotiable field
    if (
      criterion.non_negotiable === true ||
      String(criterion.non_negotiable) === 'Non-negotiable'
    ) {
      return true;
    }

    // Check for foundational skills that are typically non-negotiable
    const label = (criterion.label || '').toLowerCase();
    const name = (criterion.name || '').toLowerCase();
    const title = (criterion.title || '').toLowerCase();

    const nonNegotiablePatterns = [
      'phonemic awareness',
      'phonics',
      'alphabet knowledge',
      'foundational skills',
      'letter recognition',
      'sound-letter correspondence',
      'decoding',
      'systematic instruction',
      'explicit instruction',
    ];

    return nonNegotiablePatterns.some(
      (pattern) =>
        label.includes(pattern) ||
        name.includes(pattern) ||
        title.includes(pattern),
    );
  }
</script>

<style lang="scss" scoped>
  .science-of-reading-section {
    margin-bottom: 2rem;
  }

  .content {
    margin-bottom: 1.5rem;
    line-height: 1.6;
  }

  .section-subtitle-link {
    cursor: pointer;

    &:hover {
      text-decoration: underline !important;
    }
  }
</style>
